import { Wave, WaveMap } from 'common/types/common';
import { useCallback, useMemo } from 'react';
import { useGetAllFilters, useGetStudySpecificRestrictions } from './filters';
import { useGetAllRestrictions } from 'common/queries/filters';
import { Study } from 'common/types/account';
import { StudyWavesMap } from 'common/types/export';
import { displayDate } from 'utils/dates';

export const useGetLastWave = (studyId: string) => {
  const { waves } = useGetStudySpecificRestrictions(studyId);

  const lastWave = useMemo(() => {
    if (!waves || waves.length === 0) {
      console.error(`No waves found for study ${studyId}`);
      return undefined;
    }

    return waves.reduce((prev: Wave, curr: Wave) => {
      const prevDate = new Date(prev.date);
      const currDate = new Date(curr.date);
      return prevDate > currDate ? prev : curr;
    });
  }, [waves, studyId]);

  return lastWave?.id;
};

// Get last N of waves
export const useGetLastWaves = (studyId: string) => {
  const { waves } = useGetStudySpecificRestrictions(studyId);

  // Memoize the sorted waves array
  const sortedWaves = useMemo(() => {
    const newWaves = [...waves];
    return newWaves?.sort((a: Wave, b: Wave) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [waves]);

  // Create a callback function that returns the IDs of the most recent waves
  const getWaveIds = useCallback(
    (numWaves: number): number[] => {
      if (!sortedWaves) return [];
      return sortedWaves.slice(0, numWaves).map(({ id }) => id);
    },
    [sortedWaves],
  );

  return getWaveIds;
};

// Return wave IDs ordered by date, with the oldest first
export const useGetWavesOrderedByDate = (studyId: string, numWaves?: number) => {
  const { waves } = useGetStudySpecificRestrictions(studyId);

  // Memoize the sorted wave IDs, with most recent waves last
  const waveIds = useMemo(() => {
    // Sort waves by date in descending order (most recent first)
    const sortedWaves = [...waves].sort((a: Wave, b: Wave) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Slice to get the most recent `numWaves` waves and reverse the slice to have most recent last
    return sortedWaves
      .slice(0, numWaves ? numWaves : sortedWaves.length)
      .reverse()
      .map(({ id }) => id);
  }, [waves, numWaves]);

  return waveIds;
};

export const useStudyWaves = (studies: Study[] | undefined): StudyWavesMap => {
  const { data, isLoading } = useGetAllRestrictions();

  const studyUuidList = useMemo(() => {
    return studies?.map(({ uuid }) => uuid);
  }, [studies]);

  const studyWaves = useMemo(() => {
    return studyUuidList
      ?.flatMap(studyUuid => data?.filter(({ study_uuid }) => study_uuid === studyUuid))
      .map(rest => ({ waves: rest?.waves, studyUuid: rest?.study_uuid }));
  }, [data, studyUuidList]);

  const studyWavesMap = useMemo(() => {
    const studyWaveMap = new Map();

    for (const item of studyWaves || []) {
      studyWaveMap.set(
        `${item.studyUuid}`,
        (item.waves || []).map(wave => ({ label: displayDate(new Date(wave.date), 'MMMM YYYY'), value: String(wave.id) })),
      );
    }

    return studyWaveMap;
  }, [studyWaves]);

  return { waves: studyWavesMap, isLoading };
};

export const useGetWavesAndCadencesMap = (studyUuid: string): WaveMap => {
  const { waves } = useGetAllFilters();
  const { cadences } = useGetStudySpecificRestrictions(studyUuid);

  return useMemo(() => {
    const map: WaveMap = {};

    waves.forEach(wave => {
      map[wave.id] = wave;
    });

    // Add cadence entries so cadence IDs resolve to a date
    // (needed when normalizeRawData has replaced wave IDs with cadence IDs)
    Object.values(cadences)
      .flat()
      .forEach(entry => {
        map[entry.id] = { id: entry.id, name: entry.name, date: new Date(entry.date) };
      });

    return map;
  }, [waves, cadences]);
};
